// src/components/dashboard/VisitsReport.jsx

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';

// Icons
import LocalHospitalRoundedIcon from '@mui/icons-material/LocalHospitalRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CleaningServicesRoundedIcon from '@mui/icons-material/CleaningServicesRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';

const initialVisitsData = [
  {
    id: 'VST-2026-081',
    client: 'Jamal Kamal',
    initial: 'JK',
    staff: 'Ali Ammar, TruEvv',
    payer: 'Blue Cross Blue Shield',
    dos: '09/14/2026',
    time: '10:00 AM - 11:00 AM',
    cptCode: '90834',
    service: 'Psychotherapy, 45 min',
    units: 1,
    chargedAmount: '$165.00',
    invoiceDate: '09/14/2026',
    status: 'Invoiced',
    statusTheme: { bg: '#EFF6FF', text: '#2563EB', border: '#BFDBFE' },
  },
  {
    id: 'VST-2026-079',
    client: 'Liam Alexander Vance',
    initial: 'LV',
    staff: 'Dr. Sarah Jenkins, MD',
    payer: 'Medicaid State Plan',
    dos: '09/13/2026',
    time: '02:30 PM - 03:30 PM',
    cptCode: '97110',
    service: 'Therapeutic Exercise',
    units: 4,
    chargedAmount: '$240.00',
    invoiceDate: '09/13/2026',
    status: 'Paid',
    statusTheme: { bg: '#ECFDF5', text: '#059669', border: '#A7F3D0' },
  },
  {
    id: 'VST-2026-074',
    client: 'Elena Sofia Rossi',
    initial: 'ER',
    staff: 'Marcus Sterling, LCSW',
    payer: 'Aetna Health Inc',
    dos: '09/11/2026',
    time: '09:00 AM - 10:00 AM',
    cptCode: '90837',
    service: 'Psychotherapy, 60 min',
    units: 1,
    chargedAmount: '$190.00',
    invoiceDate: '09/12/2026',
    status: 'Pending Claim',
    statusTheme: { bg: '#FFFBEB', text: '#D97706', border: '#FDE68A' },
  },
  {
    id: 'VST-2026-068',
    client: 'Noah William Dubois',
    initial: 'ND',
    staff: 'Rachel Green, PsyD',
    payer: 'UnitedHealthcare',
    dos: '09/09/2026',
    time: '11:15 AM - 12:15 PM',
    cptCode: '90791',
    service: 'Psychiatric Diagnostic Eval',
    units: 1,
    chargedAmount: '$275.00',
    invoiceDate: '09/10/2026',
    status: 'Invoiced',
    statusTheme: { bg: '#EFF6FF', text: '#2563EB', border: '#BFDBFE' },
  },
  {
    id: 'VST-2026-062',
    client: 'Astrid Maeve Lindholm',
    initial: 'AL',
    staff: 'David K. Hoffman, BCBA',
    payer: 'Private Pay / Out of Pocket',
    dos: '09/08/2026',
    time: '03:00 PM - 04:30 PM',
    cptCode: 'H2019',
    service: 'Behavioral Support Session',
    units: 6,
    chargedAmount: '$310.00',
    invoiceDate: '09/08/2026',
    status: 'Needs Review',
    statusTheme: { bg: '#FEF2F2', text: '#DC2626', border: '#FECACA' },
  },
  {
    id: 'VST-2026-059',
    client: 'Mateo Hiroshi Tanaka',
    initial: 'MT',
    staff: 'Hannah Abbott, MSW',
    payer: 'Cigna Behavioral Health',
    dos: '09/05/2026',
    time: '01:00 PM - 02:00 PM',
    cptCode: '90847',
    service: 'Family Psychotherapy',
    units: 1,
    chargedAmount: '$180.00',
    invoiceDate: '09/06/2026',
    status: 'Paid',
    statusTheme: { bg: '#ECFDF5', text: '#059669', border: '#A7F3D0' },
  },
];

const VisitsReport = () => {
  const [clientName, setClientName] = useState('');
  const [quickRange, setQuickRange] = useState('');
  const [startDate, setStartDate] = useState('09/01/2026');
  const [endDate, setEndDate] = useState('09/14/2026');

  // Dynamic Column Checkboxes
  const [displayOptions, setDisplayOptions] = useState({
    showStaff: true,
    showPayer: true,
    showInvoiceDate: true,
    chargedAmount: true,
    showTimeOnDos: true,
  });

  const handleCheckboxChange = (field) => {
    setDisplayOptions((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleClear = () => {
    setClientName('');
    setQuickRange('');
    setStartDate('');
    setEndDate('');
    setDisplayOptions({
      showStaff: true,
      showPayer: true,
      showInvoiceDate: true,
      chargedAmount: true,
      showTimeOnDos: true,
    });
  };

  return (
    <Box sx={{ width: '100%', pb: 6, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      
      {/* 1. Top Header & Quick Actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '14px',
              background: 'linear-gradient(135deg, #0B2540 0%, #1E3A5F 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#FFFFFF',
              boxShadow: '0 6px 16px rgba(11, 37, 64, 0.25)',
              flexShrink: 0,
            }}
          >
            <LocalHospitalRoundedIcon sx={{ fontSize: '1.6rem', color: '#38BDF8' }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: '1.35rem',
                fontWeight: 800,
                color: '#0B2540',
                letterSpacing: '-0.3px',
                lineHeight: 1.15,
              }}
            >
              Visits Report
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: '#64748B', fontWeight: 500, mt: 0.3 }}>
              Track patient visits, encounters, billing units and service details
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadOutlinedIcon />}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.8rem',
              color: '#0B2540',
              borderColor: '#CBD5E1',
              backgroundColor: '#FFFFFF',
              '&:hover': { borderColor: '#0B2540', backgroundColor: '#F8FAFC' },
            }}
          >
            Export Encounter CSV
          </Button>
        </Box>
      </Box>

      {/* 2. Top Metric KPI Summary Strip */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: '12px',
              border: '1px solid #E2E8F0',
              backgroundColor: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontWeight: 700, textTransform: 'uppercase' }}>
                Total Encounters
              </Typography>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#0B2540', mt: 0.2 }}>
                6 Visits
              </Typography>
            </Box>
            <Avatar sx={{ bgcolor: '#EFF6FF', color: '#2563EB', width: 40, height: 40 }}>
              <ReceiptLongRoundedIcon sx={{ fontSize: '1.25rem' }} />
            </Avatar>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: '12px',
              border: '1px solid #E2E8F0',
              backgroundColor: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontWeight: 700, textTransform: 'uppercase' }}>
                Billable Units
              </Typography>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#0B2540', mt: 0.2 }}>
                14 Units
              </Typography>
            </Box>
            <Avatar sx={{ bgcolor: '#ECFDF5', color: '#059669', width: 40, height: 40 }}>
              <CheckCircleOutlineRoundedIcon sx={{ fontSize: '1.25rem' }} />
            </Avatar>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: '12px',
              border: '1px solid #E2E8F0',
              backgroundColor: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontWeight: 700, textTransform: 'uppercase' }}>
                Total Billed
              </Typography>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#0B2540', mt: 0.2 }}>
                $1,360.00
              </Typography>
            </Box>
            <Avatar sx={{ bgcolor: '#F0FDF4', color: '#16A34A', width: 40, height: 40 }}>
              <AttachMoneyRoundedIcon sx={{ fontSize: '1.3rem' }} />
            </Avatar>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: '12px',
              border: '1px solid #E2E8F0',
              backgroundColor: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontWeight: 700, textTransform: 'uppercase' }}>
                Needs Audit
              </Typography>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#DC2626', mt: 0.2 }}>
                1 Record
              </Typography>
            </Box>
            <Avatar sx={{ bgcolor: '#FEF2F2', color: '#DC2626', width: 40, height: 40 }}>
              <HourglassEmptyRoundedIcon sx={{ fontSize: '1.25rem' }} />
            </Avatar>
          </Paper>
        </Grid>
      </Grid>

      {/* 3. Upgraded Filter Visits Card */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: '24px 30px' },
          borderRadius: '16px',
          border: '1px solid #E2E8F0',
          backgroundColor: '#FFFFFF',
          boxShadow: '0 4px 20px -6px rgba(15, 23, 42, 0.05)',
          mb: 4,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 2.5 }}>
          <SearchRoundedIcon sx={{ fontSize: '1.35rem', color: '#0B2540' }} />
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#0B2540' }}>
            Filter Visits
          </Typography>
        </Box>

        {/* Row 1: Client Name & Quick Range */}
        <Grid container spacing={3} sx={{ mb: 2.5 }}>
          <Grid item xs={12} md={6}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#0F172A', mb: 0.8 }}>
              Client Name
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                displayEmpty
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                IconComponent={KeyboardArrowDownRoundedIcon}
                renderValue={(selected) => {
                  if (!selected) {
                    return <span style={{ color: '#94A3B8', fontSize: '0.84rem' }}>Search and select patient...</span>;
                  }
                  return selected;
                }}
                sx={{
                  borderRadius: '10px',
                  backgroundColor: '#F8FAFC',
                  fontSize: '0.84rem',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#CBD5E1' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0B2540' },
                }}
              >
                <MenuItem value="" disabled>Search and select patient...</MenuItem>
                {initialVisitsData.map((v, idx) => (
                  <MenuItem key={idx} value={v.client}>{v.client}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#0F172A', mb: 0.8 }}>
              Quick Range
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                displayEmpty
                value={quickRange}
                onChange={(e) => setQuickRange(e.target.value)}
                IconComponent={KeyboardArrowDownRoundedIcon}
                renderValue={(selected) => {
                  if (!selected) {
                    return <span style={{ color: '#94A3B8', fontSize: '0.84rem' }}>Select date range...</span>;
                  }
                  return selected;
                }}
                sx={{
                  borderRadius: '10px',
                  backgroundColor: '#F8FAFC',
                  fontSize: '0.84rem',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#CBD5E1' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0B2540' },
                }}
              >
                <MenuItem value="" disabled>Select date range...</MenuItem>
                <MenuItem value="Today">Today</MenuItem>
                <MenuItem value="This Week">This Week</MenuItem>
                <MenuItem value="Last 7 Days">Last 7 Days</MenuItem>
                <MenuItem value="This Month">This Month</MenuItem>
                <MenuItem value="Last Month">Last Month</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Row 2: Date of Service (DOS) */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#0F172A', mb: 0.8 }}>
            Date of Service:
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CalendarTodayOutlinedIcon sx={{ fontSize: '1rem', color: '#64748B' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: '160px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontSize: '0.84rem',
                  backgroundColor: '#F8FAFC',
                  '& fieldset': { borderColor: '#E2E8F0' },
                  '&:hover fieldset': { borderColor: '#CBD5E1' },
                  '&.Mui-focused fieldset': { borderColor: '#0B2540' },
                },
                '& input': { py: '8.5px', px: '12px' },
              }}
            />

            <Typography sx={{ fontSize: '0.85rem', color: '#64748B', fontWeight: 600 }}>
              to
            </Typography>

            <TextField
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CalendarTodayOutlinedIcon sx={{ fontSize: '1rem', color: '#64748B' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: '160px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontSize: '0.84rem',
                  backgroundColor: '#F8FAFC',
                  '& fieldset': { borderColor: '#E2E8F0' },
                  '&:hover fieldset': { borderColor: '#CBD5E1' },
                  '&.Mui-focused fieldset': { borderColor: '#0B2540' },
                },
                '& input': { py: '8.5px', px: '12px' },
              }}
            />
          </Box>
        </Box>

        {/* Row 3: Display Options (Checkboxes with Pill Bolding) */}
        <Box sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#0F172A', mb: 0.8 }}>
            Display Options
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 3 }, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={displayOptions.showStaff}
                  onChange={() => handleCheckboxChange('showStaff')}
                  sx={{ color: '#0B2540', p: 0.6, '&.Mui-checked': { color: '#0B2540' } }}
                />
              }
              label={<Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: '#0F172A' }}>Show staff</Typography>}
              sx={{ m: 0 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={displayOptions.showPayer}
                  onChange={() => handleCheckboxChange('showPayer')}
                  sx={{ color: '#0B2540', p: 0.6, '&.Mui-checked': { color: '#0B2540' } }}
                />
              }
              label={<Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: '#0F172A' }}>Show payer</Typography>}
              sx={{ m: 0 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={displayOptions.showInvoiceDate}
                  onChange={() => handleCheckboxChange('showInvoiceDate')}
                  sx={{ color: '#0B2540', p: 0.6, '&.Mui-checked': { color: '#0B2540' } }}
                />
              }
              label={<Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: '#0F172A' }}>Show invoice date</Typography>}
              sx={{ m: 0 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={displayOptions.chargedAmount}
                  onChange={() => handleCheckboxChange('chargedAmount')}
                  sx={{ color: '#0B2540', p: 0.6, '&.Mui-checked': { color: '#0B2540' } }}
                />
              }
              label={<Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: '#0F172A' }}>Charged amount</Typography>}
              sx={{ m: 0 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={displayOptions.showTimeOnDos}
                  onChange={() => handleCheckboxChange('showTimeOnDos')}
                  sx={{ color: '#0B2540', p: 0.6, '&.Mui-checked': { color: '#0B2540' } }}
                />
              }
              label={<Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: '#0F172A' }}>Show Time on DOS</Typography>}
              sx={{ m: 0 }}
            />
          </Box>
        </Box>

        {/* Row 4: Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
          <Button
            variant="contained"
            disableElevation
            startIcon={<SearchRoundedIcon sx={{ fontSize: '1.1rem' }} />}
            sx={{
              backgroundColor: '#0B2540',
              color: '#FFFFFF',
              textTransform: 'none',
              fontSize: '0.84rem',
              fontWeight: 700,
              borderRadius: '8px',
              px: 3.2,
              py: '8px',
              '&:hover': { backgroundColor: '#1E3A5F' },
            }}
          >
            Search
          </Button>

          <Button
            onClick={handleClear}
            variant="outlined"
            disableElevation
            startIcon={<CleaningServicesRoundedIcon sx={{ fontSize: '1.05rem' }} />}
            sx={{
              borderColor: '#E2E8F0',
              color: '#475569',
              textTransform: 'none',
              fontSize: '0.84rem',
              fontWeight: 700,
              borderRadius: '8px',
              px: 3,
              py: '8px',
              '&:hover': { borderColor: '#CBD5E1', backgroundColor: '#F8FAFC' },
            }}
          >
            Clear
          </Button>
        </Box>
      </Paper>

      {/* =========================================================
          4. MODERN & UNIQUE VISITS ENCOUNTER DATA TABLE
         ========================================================= */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: '16px',
          border: '1px solid #E2E8F0',
          backgroundColor: '#FFFFFF',
          boxShadow: '0 4px 18px -4px rgba(15, 23, 42, 0.05)',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: '#F8FAFC' }}>
              <TableRow>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', py: 1.6 }}>CLIENT</TableCell>
                {displayOptions.showStaff && (
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', py: 1.6 }}>ASSIGNED STAFF</TableCell>
                )}
                {displayOptions.showPayer && (
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', py: 1.6 }}>PAYER</TableCell>
                )}
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', py: 1.6 }}>DATE OF SERVICE (DOS)</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', py: 1.6 }}>SERVICE / CPT</TableCell>
                {displayOptions.showInvoiceDate && (
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', py: 1.6 }}>INVOICED</TableCell>
                )}
                {displayOptions.chargedAmount && (
                  <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', py: 1.6 }}>CHARGED</TableCell>
                )}
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', py: 1.6 }}>STATUS</TableCell>
                <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', py: 1.6 }}>ACTION</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {initialVisitsData.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{
                    transition: 'background-color 0.15s ease',
                    '&:last-child td': { borderBottom: 0 },
                  }}
                >
                  {/* Client Identifier */}
                  <TableCell sx={{ py: 1.8 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4 }}>
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: '#0B2540',
                          color: '#38BDF8',
                          fontSize: '0.85rem',
                          fontWeight: 700,
                        }}
                      >
                        {row.initial}
                      </Avatar>
                      <Box>
                        <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: '#0F172A', lineHeight: 1.2 }}>
                          {row.client}
                        </Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', fontFamily: 'monospace' }}>
                          #{row.id}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  {/* Staff */}
                  {displayOptions.showStaff && (
                    <TableCell sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#334155' }}>
                      {row.staff}
                    </TableCell>
                  )}

                  {/* Payer */}
                  {displayOptions.showPayer && (
                    <TableCell sx={{ fontSize: '0.82rem', color: '#475569' }}>
                      {row.payer}
                    </TableCell>
                  )}

                  {/* DOS + Time */}
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography sx={{ fontSize: '0.83rem', fontWeight: 700, color: '#0F172A' }}>
                        {row.dos}
                      </Typography>
                      {displayOptions.showTimeOnDos && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
                          <AccessTimeRoundedIcon sx={{ fontSize: '0.82rem', color: '#64748B' }} />
                          <Typography sx={{ fontSize: '0.74rem', color: '#64748B' }}>
                            {row.time}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </TableCell>

                  {/* CPT Service */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={row.cptCode}
                        size="small"
                        sx={{
                          height: '22px',
                          fontSize: '0.72rem',
                          fontWeight: 800,
                          backgroundColor: '#EFF6FF',
                          color: '#2563EB',
                        }}
                      />
                      <Typography sx={{ fontSize: '0.78rem', color: '#475569' }}>
                        {row.service} <strong style={{ color: '#0F172A' }}>({row.units}u)</strong>
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Invoiced */}
                  {displayOptions.showInvoiceDate && (
                    <TableCell sx={{ fontSize: '0.8rem', color: '#64748B' }}>
                      {row.invoiceDate}
                    </TableCell>
                  )}

                  {/* Charged Amount */}
                  {displayOptions.chargedAmount && (
                    <TableCell align="right">
                      <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: '#0B2540' }}>
                        {row.chargedAmount}
                      </Typography>
                    </TableCell>
                  )}

                  {/* Status */}
                  <TableCell>
                    <Chip
                      label={row.status}
                      size="small"
                      sx={{
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        backgroundColor: row.statusTheme.bg,
                        color: row.statusTheme.text,
                        border: `1px solid ${row.statusTheme.border}`,
                        borderRadius: '6px',
                      }}
                    />
                  </TableCell>

                  {/* Actions */}
                  <TableCell align="center">
                    <Tooltip title="View Encounter">
                      <IconButton size="small" sx={{ color: '#0B2540', '&:hover': { bgcolor: '#EFF6FF' } }}>
                        <VisibilityOutlinedIcon sx={{ fontSize: '1.2rem' }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

    </Box>
  );
};

export default VisitsReport;